import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { EmailAuthProvider } from 'firebase/auth';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { unwrapNewApiEnvelope } from '../shared/utils/new-api-envelope.util';
import { environment } from '../../environments/environment';
import { FEATURES_DASHBOARD } from '../interface/features.interface';
import { ROUTES } from '../shared/sidebar/menu-items';
import { DataService } from './data.service';
import { MessagingService } from './messaging.service';
import { WhiteLabelService } from './white-label.service';
import { NavigationService } from './navigation.service';
import { MenuRegenerationService } from './menu-regeneration.service';
import { ModalDatabaseUseAuthorizationComponent } from 'src/app/shared/modals/modal-database-use-authorization/modal-database-use-authorization.component';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AssistantValidateEmailResponse, UserRoles, UserSession } from '../interface/users.interface';
import { ApiDetailResponse } from '../interface/data.interface';
import { CurrentUser } from '../interface/currentUser.interface';
import { NavigationUser } from '../interface/navigation.interface';
import { SessionGrantService } from './session-grant.service';
import { mergeUserSessionIntoCurrentUser } from '../shared/utils/merge-user-session-into-current-user';

export const HEADER_FB_TOKEN = 'fb-token';

@Injectable()
export class AuthService extends DataService {
  messaging: any;
  currentMessage = new BehaviorSubject(null);
  userId = null;
  dialogRefDatabaseUseAuthorization: MatDialogRef<ModalDatabaseUseAuthorizationComponent, any> | null = null;

  constructor(
    private http: HttpClient,
    public afAuth: AngularFireAuth,
    private messagingService: MessagingService,
    protected injector: Injector,
    private router: Router,
    private toastr: ToastrService,
    private translate: TranslateService,
    protected store: Store<any>,
    private matDialog: MatDialog,
    private whiteLabelService: WhiteLabelService,
  ) {
    super(injector, store);
    this.dialogRefDatabaseUseAuthorization = null;
  }

  async doLogin(params) {
    try {
      return new Promise((resolve, reject) => {
        this.afAuth
          .signInWithEmailAndPassword(params.email, params.password)
          .then((res) =>
            res.user.getIdToken().then(async (token) => {
              const fcmToken: any = await this.messagingService.requestPermission();
              this.messagingService.receiveMessage();
              return this.doLoginKaus(params.email, token, fcmToken)
                .then(async (r: any) => {
                  let user = r.user;
                  let userBuilding = r.newFormat.userBuilding;
                  // VDS-3458: superuser stays out of multisession — wipe residual vault
                  // instead of rejecting with actionDeniedByRole (login loop).
                  if (user.superUser) {
                    await this.clearMultisessionVaultForSuperUserLogin();
                  }
                  this.userId = user.id;
                  if (user.flag && !user.flag.changePassword) return reject({ notChangePassword: true });

                  const buildings = r.user.buildings;
                  if (buildings.length > 0 && !user.superUser) {
                    if (buildings[0].status !== 'active') return reject({ inactive: true, message: 'toastrMessage.error.buildingInactive' });
                    if (buildings[0].userStatus !== 'active') return reject({ inactive: true, message: 'toastrMessage.error.userInactive' });
                  }

                  user.token = r.token;
                  user.fbToken = token;
                  user.roles = this.resolveLoginRoles(buildings, userBuilding, r.newFormat);
                  user.photoUrl = user.photoUrl;
                  user.isAuth = true;
                  user.features = this.mapFeatures(userBuilding.features);
                  // Backend puede enviar unit poblado sin unit_id en JSON; alinear siempre con la relación userBuilding
                  if (user.buildings?.length && userBuilding) {
                    const ub: any = userBuilding;
                    const fromUnitObj =
                      ub.unit && typeof ub.unit === 'object'
                        ? ub.unit.id ?? ub.unit._id
                        : undefined;
                    const resolvedUnitId =
                      (typeof ub.unit_id === 'string' && ub.unit_id) ||
                      (ub.unit_id && typeof ub.unit_id === 'object' && (ub.unit_id.id ?? ub.unit_id._id)) ||
                      (typeof ub.unit === 'string' && ub.unit) ||
                      fromUnitObj;
                    if (resolvedUnitId) {
                      user.buildings[0].unitId = String(resolvedUnitId);
                    }
                    if (ub.unit && typeof ub.unit === 'object' && ub.unit.number != null) {
                      user.buildings[0].unit = {
                        id: String(ub.unit.id ?? ub.unit._id ?? user.buildings[0].unitId),
                        number: ub.unit.number,
                      };
                    }
                  }
                  this.setUser(user);
                  await this.mintAndPersistSessionGrant(user);
                  await this.rehydrateCurrentUserFromSession();
                  if (buildings?.length) {
                    const refreshed = this.getUser();
                    const building = refreshed?.buildings?.[0] ?? buildings[0];
                    this.whiteLabelService.setSessionLoginPathFromBuilding(building);
                    this.whiteLabelService.applyFromBuilding(building);
                  }
                  const language: string = user.i18n;
                  if (language) this.setLanguage(language.slice(0, 2));
                  return resolve(true);
                })
                .catch((err) => {
                  console.log('-> err: ', err);
                  return reject(err);
                });
            }),
          )
          .catch((err) => {
            console.log(err);
            reject(err);
          });
      });
    } catch (err) {
      console.log('-- err: ', err);
    }
  }

  async doLoginAdonis(email, fbToken, fcmToken) {
    const headers = new HttpHeaders().set('x-platform', 'webpage');
    return this.http
      .post(`${environment.apiUrl}/user/login`, { email, fbToken, fcmToken }, { headers })
      .toPromise()
      .catch((err) => {
        return Promise.reject(err);
      });
  }

  async doLoginKaus(email: string, fbToken: string, fcmToken?: string) {
    const headers = new HttpHeaders().set('x-platform', 'webpage');
    let body: any = {
      email: email,
      fbToken: fbToken,
    };
    if (!!fcmToken) body = { fcmToken: fcmToken, ...body };
    return this.http
      .post(`/api/auth/v1/login`, body, { headers })
      .toPromise()
      .catch((err) => {
        console.log(err);
        return Promise.reject(err);
      });
  }

  doLogoutFirebase() {
    const loginPath = this.prepareLogoutLoginRedirect();
    // Prefer switch-to-next when possible; only signs out Firebase if no sessions remain.
    void this.finishScopedLogoutOrSwitch(loginPath);
  }

  // Refresca el token de Firebase sin hacer login completo
  async refreshFirebaseToken(): Promise<string | null> {
    try {
      const currentUser = await this.afAuth.currentUser;
      if (currentUser) {
        const token = await currentUser.getIdToken(true);
        const user = this.getUser();
        if (user) {
          user.fbToken = token;
          this.setUser(user);
        }
        return token;
      }
      return null;
    } catch (error) {
      console.error('Error refrescando token de Firebase:', error);
      return null;
    }
  }

  /**
   * Si la sesión activa de Firebase es la misma cuenta que `user` (mismo email),
   * fuerza un ID token fresco y actualiza currentUser y usersSessions.
   */
  async syncFirebaseTokenIfSessionMatches(user: any): Promise<void> {
    try {
      const fbUser = await this.afAuth.currentUser;
      if (!fbUser?.email || !user?.email) return;
      const a = fbUser.email.toLowerCase().trim();
      const b = user.email.toLowerCase().trim();
      if (a !== b) return;
      const token = await fbUser.getIdToken(true);
      user.fbToken = token;
      this.setUser(user);
      if (!user.superUser) {
        this.setUserInMultisesion(user);
      }
    } catch (error) {
      console.error('Error sincronizando token Firebase al activar sesión:', error);
    }
  }

  /**
   * Mint a session grant after a successful password login (additive; login unchanged).
   * VDS-3458: superuser mints for solo cold-start recovery but is never pushed into
   * the multi-account `usersSessions` vault.
   */
  async mintAndPersistSessionGrant(user: any): Promise<void> {
    if (!user?.fbToken) {
      return;
    }
    try {
      const sessionGrantService = this.injector.get(SessionGrantService);
      const minted = await sessionGrantService.mint(
        user.fbToken,
        user.sessionGrant,
      );
      if (minted?.sessionGrant) {
        user.sessionGrant = minted.sessionGrant;
        this.setUser(user);
        if (!user.superUser) {
          this.setUserInMultisesion(user);
        }
      }
    } catch (error) {
      console.error('Error minting session grant after login:', error);
    }
  }

  /**
   * Switch Firebase identity to `user` via session grant (VDS-3308).
   * Returns false if grant missing/invalid — caller should not activate blindly.
   */
  async activateAccountWithGrant(user: any): Promise<boolean> {
    if (!user) {
      return false;
    }
    try {
      const sessionGrantService = this.injector.get(SessionGrantService);
      if (user.sessionGrant) {
        const exchanged = await sessionGrantService.exchange(user.sessionGrant);
        if (!exchanged?.customToken) {
          return false;
        }
        const cred = await this.afAuth.signInWithCustomToken(exchanged.customToken);
        const fbEmail = (cred.user?.email ?? '').toLowerCase().trim();
        const targetEmail = (user.email ?? '').toLowerCase().trim();
        // Corrupted vault grant (minted under another account) must not activate:
        // UI would show target roles while Fb-Token belongs to someone else.
        if (fbEmail && targetEmail && fbEmail !== targetEmail) {
          console.error(
            'Session grant exchanged to a different Firebase email than the target account',
            { fbEmail, targetEmail },
          );
          return false;
        }
        const token = await cred.user.getIdToken(true);
        user.fbToken = token;
        // Persist identity BEFORE mint: TokenInterceptor overwrites Fb-Token from
        // localStorage; minting while currentUser is still the previous account
        // attaches that account's grant onto this snapshot (proprietor↔admin desync).
        this.setUser(user);
        // VDS-3458: never place superuser into the multi-account vault.
        if (!user.superUser) {
          this.setUserInMultisesion(user);
        }
        await this.mintAndPersistSessionGrant(user);
        await this.rehydrateCurrentUserFromSession();
        return true;
      }
      await this.syncFirebaseTokenIfSessionMatches(user);
      const fbUser = await this.afAuth.currentUser;
      const same =
        fbUser?.email &&
        user?.email &&
        fbUser.email.toLowerCase().trim() === user.email.toLowerCase().trim();
      if (same) {
        await this.rehydrateCurrentUserFromSession();
      }
      return !!same;
    } catch (error) {
      console.error('Error activating account with session grant:', error);
      return false;
    }
  }

  // Verifica si Firebase tiene una sesión activa
  async hasActiveFirebaseSession(): Promise<boolean> {
    try {
      const currentUser = await this.afAuth.currentUser;
      return currentUser !== null;
    } catch (error) {
      console.error('Error verificando sesión Firebase:', error);
      return false;
    }
  }

  /**
   * VDS-3456 — Boot / guard recovery: rehydrate Firebase from `sessionGrant` when the
   * local vault snapshot exists but Firebase Auth is missing or mismatched.
   */
  async tryRestoreSessionFromVault(snapshot?: Partial<CurrentUser> | null): Promise<boolean> {
    const user = snapshot ?? this.getVaultSnapshotForRecovery();
    // VDS-3458: superuser solo-cuenta also recovers via grant (still no multisession).
    if (!user || !user.sessionGrant) {
      return false;
    }

    try {
      const fbUser = await this.afAuth.currentUser;
      const curEmail = (user.email ?? '').toLowerCase().trim();
      const fbEmail = (fbUser?.email ?? '').toLowerCase().trim();

      if (fbUser && curEmail && fbEmail === curEmail) {
        const token = await this.refreshFirebaseToken();
        return !!token;
      }

      return this.activateAccountWithGrant(user);
    } catch (error) {
      console.error('tryRestoreSessionFromVault failed:', error);
      return false;
    }
  }

  /** Reads `currentUser` for grant recovery even when `getUser()` is strict. */
  private getVaultSnapshotForRecovery(): Partial<CurrentUser> | null {
    const fromService = this.getUser();
    if (fromService?.sessionGrant) {
      return fromService;
    }
    try {
      const raw = localStorage.getItem('currentUser');
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(raw) as Partial<CurrentUser>;
      return parsed?.sessionGrant ? parsed : null;
    } catch {
      return null;
    }
  }

  /**
   * Clears the active account only: `currentUser` + that entry in `usersSessions`.
   * Other multisession accounts remain (VDS-3330).
   */
  private clearCurrentUserSession(): void {
    const user = this.getUser();
    const email = user?.email;
    const id = user?.id;
    this.clearUserCache();
    if (email || id) {
      this.removeUserFromMultisession({ email, id });
    }
    localStorage.removeItem('currentUser');
  }

  /** Clears every local session (`currentUser` + `usersSessions`). */
  private clearAllUserSessions(): void {
    this.clearUserCache();
    localStorage.removeItem('currentUser');
    localStorage.removeItem('usersSessions');
  }

  /**
   * VDS-3458 — Superuser cannot coexist with multisession.
   * Revoke residual vault grants and wipe `usersSessions` so login can proceed
   * (replaces the old actionDeniedByRole reject loop).
   */
  private async clearMultisessionVaultForSuperUserLogin(): Promise<void> {
    const rawVault = localStorage.getItem('usersSessions');
    if (!rawVault || rawVault.length === 0) {
      return;
    }
    try {
      const sessions = this.getUserInMultisession() ?? [];
      const grants = [
        this.getUser()?.sessionGrant,
        ...sessions.map((s: { sessionGrant?: string }) => s?.sessionGrant),
      ].filter((g): g is string => !!g);
      if (grants.length > 0) {
        await this.injector.get(SessionGrantService).revokeMany(grants);
      }
    } catch (error) {
      console.error('Error clearing multisession vault for superuser login:', error);
    }
    localStorage.removeItem('usersSessions');
  }

  /**
   * After scoped logout: if other accounts remain, activate the next one via grant
   * (Firebase identity follows). If none remain → Firebase signOut + login.
   */
  private async activateNextMultisessionAccount(user: NavigationUser): Promise<void> {
    const ok = await this.activateAccountWithGrant(user);
    if (!ok) {
      this.setUser(user);
    }

    const building = user.buildings?.[0];
    if (building) {
      this.whiteLabelService.setSessionLoginPathFromBuilding(building);
      this.whiteLabelService.applyFromBuilding(building);
    }

    const navigationService = this.injector.get(NavigationService);
    const menuRegenerationService = this.injector.get(MenuRegenerationService);
    navigationService.navigateToUserRoute(user);
    menuRegenerationService.triggerMenuRegeneration();

    this.toastr.success(this.translate.instant('toastrMessage.success.accountChanged'));

    setTimeout(() => {
      window.location.reload();
    }, 100);
  }

  private async finishScopedLogoutOrSwitch(loginPath: string): Promise<void> {
    const current = this.getUser();
    if (current?.sessionGrant) {
      await this.injector.get(SessionGrantService).revokeCurrent(current.sessionGrant);
    }
    this.clearCurrentUserSession();
    const remaining = this.getUserInMultisession() ?? [];
    if (remaining.length > 0) {
      const next = remaining[remaining.length - 1] as NavigationUser;
      await this.activateNextMultisessionAccount(next);
      return;
    }
    await this.afAuth.signOut();
    this.router.navigateByUrl(loginPath);
  }

  /** Branding y ruta de login post-logout: host dedicado > building > default. */
  private prepareLogoutLoginRedirect(): string {
    const building = this.getBuilding();
    this.whiteLabelService.setSessionLoginPathFromBuilding(building);
    if (!this.whiteLabelService.applyPreLoginForCurrentHost()) {
      this.whiteLabelService.applyFromBuilding(building);
    }
    return this.whiteLabelService.getLoginRedirectUrl();
  }

  doLogoutV2(fcmToken) {
    return this.http.post(`${environment.apiUrl}/auth/v1/logout`, { fcmToken });
  }

  handleLogoutError(err) {
    console.error(err);
    this.toastr.error(this.translate.instant('authentication.error.logout'));
  }

  private finishLocalLogout(loginPath: string, clearAll: boolean): void {
    if (clearAll) {
      void this.finishLogoutAll(loginPath);
      return;
    }
    // Scoped: switch to next session without Firebase signOut when possible.
    void this.finishScopedLogoutOrSwitch(loginPath);
  }

  private async finishLogoutAll(loginPath: string): Promise<void> {
    try {
      const sessionGrantService = this.injector.get(SessionGrantService);
      const sessions = this.getUserInMultisession() ?? [];
      const grants = [
        this.getUser()?.sessionGrant,
        ...sessions.map((s: { sessionGrant?: string }) => s?.sessionGrant),
      ].filter((g): g is string => !!g);
      await sessionGrantService.revokeMany(grants);
    } catch (error) {
      console.error('Error revoking session grants on logout all:', error);
    }
    await this.afAuth.signOut();
    this.clearAllUserSessions();
    this.router.navigateByUrl(loginPath);
  }

  /**
   * Logout of the active account only. Keeps other `usersSessions` entries and
   * activates the next one when present (otherwise login).
   */
  async doLogout() {
    await this.runLogoutFlow(false);
  }

  /**
   * Logout all multisession accounts (header "cerrar todas").
   */
  async doLogoutAll() {
    await this.runLogoutFlow(true);
  }

  private async runLogoutFlow(clearAll: boolean): Promise<void> {
    const loginPath = this.prepareLogoutLoginRedirect();
    try {
      const fcmToken: any = await this.messagingService.requestPermission();
      this.doLogoutV2(fcmToken).subscribe({
        next: () => this.finishLocalLogout(loginPath, clearAll),
        error: (err) => {
          this.handleLogoutError(err);
          this.finishLocalLogout(loginPath, clearAll);
        },
      });
    } catch (err) {
      this.handleLogoutError(err);
      this.finishLocalLogout(loginPath, clearAll);
    }
  }

  async doLogoutAsync() {
    const loginPath = this.prepareLogoutLoginRedirect();

    const fcmToken: any = await this.messagingService.requestPermission();
    try {
      await this.doLogoutV2(fcmToken).toPromise();
    } catch {
      // still finish local scoped/switch even if API logout fails
    }
    await this.finishScopedLogoutOrSwitch(loginPath);
  }

  async doLogoutAdonis() {
    const fcmToken = await this.messagingService.requestPermission();
    return this.http
      .post(`${environment.apiUrl}/user/logout`, { fcmToken })
      .toPromise()
      .then((res) => {
        return res;
      })
      .catch((err) => {
        return Promise.reject(err);
      });
  }

  sendPasswordResetEmail(email: string): Promise<string | void> {
    return new Promise((resolve, reject) => {
      this.http
        .post<{ useClientFirebase?: boolean; code?: number }>(`${environment.apiUrl}/public/v1/user/forgot-password`, {
          email: email.toLowerCase().trim(),
        })
        .toPromise()
        .then((res) => {
          // Solo Firebase si el API lo pide de forma explícita.
          // Si ya envió PasswordResetRequestCustom, NO llamar a sendPasswordResetEmail:
          // eso genera otro oobCode e invalida el link del correo Custom.
          if (res && res.useClientFirebase === true) {
            const actionCodeSettings = {
              url: `${environment.baseUrl}/#/authentication/login`,
              handleCodeInApp: false,
            };
            return this.afAuth
              .sendPasswordResetEmail(email, actionCodeSettings)
              .then(() => resolve())
              .catch((err) => reject(err));
          }
          return resolve();
        })
        .catch((err) => {
          // Tampoco fallback silencioso a Firebase aquí: invalidaría un Custom
          // que el backend pudo haber encolado antes de fallar la respuesta HTTP.
          reject(err);
        });
    });
  }

  resetPassword(currentPassword, newPassword) {
    return new Promise((resolve, reject) => {
      this.afAuth.currentUser.then((user: any) => {
        const credential = EmailAuthProvider.credential(user.email, currentPassword);
        user
          .reauthenticateWithCredential(credential)
          .then(() => {
            user
              .updatePassword(newPassword)
              .then(() => {
                this.changeFlagPassword(this.userId, { changePassword: true })
                  .then(() => {
                    resolve(true);
                  })
                  .catch((err) => reject(err));
              })
              .catch((err) => reject(err));
          })
          .catch((err) => reject(err));
      });
    });
  }

  sendResetPassword(currentPassword) {
    return new Promise((resolve, reject) => {
      this.afAuth.currentUser.then((user: any) => {
        const credential = EmailAuthProvider.credential(user.email, currentPassword);
        user
          .reauthenticateWithCredential(credential)
          .then(() => {
            this.sendPasswordResetEmail(user.email)
              .then(() => {
                resolve(true);
              })
              .catch(() => reject(false));
          })
          .catch((err) => reject(err));
      });
    });
  }

  validCurrentPassword(currentPassword) {
    return this.afAuth.currentUser.then((user: any) => {
      const cred = EmailAuthProvider.credential(user.email, currentPassword);
      return user.reauthenticateWithCredential(cred);
    });
  }

  changeFlagPassword(id, params) {
    return this.http
      .put(`${environment.apiUrl}/user/update-change-password/${id}`, params)
      .toPromise()
      .then((res) => {
        return res;
      })
      .catch((err) => {
        console.log(err);
        return Promise.reject(err);
      });
  }

  mapFeatures(featuresList) {
    let features = [];
    if (!featuresList) return features;
    featuresList.forEach((feature) => {
      const featureDashboard = FEATURES_DASHBOARD.find((element) => element.code === feature.feature.code);
      if (featureDashboard) {
        features.push({
          feature: featureDashboard.feature,
          enable: feature.enable,
        });
      }
    });
    return features;
  }

  getUserFirstRoute(user) {
    let route = '/app';
    const feature = user.features.find((feature) => feature.enable === true);
    if (!feature || !feature.feature[0]) return route;
    route = ROUTES[feature.feature[0]].path;
    return route;
  }

  openModalDatabaseUseAuthorization() {
    if (this.dialogRefDatabaseUseAuthorization) return;

    const building = this.getBuilding();
    const user = this.getUser();

    if (!building || !user) return;

    if (!user.roles.includes(UserRoles.admin) || user.email.includes('@getnada') || user.email.includes('@munily')) return;

    if (building && building?.config?.certificationContent?.enable) {
      return;
    }

    this.dialogRefDatabaseUseAuthorization = this.matDialog.open(ModalDatabaseUseAuthorizationComponent, {
      data: { building: { id: building.id, name: building.name } },
      panelClass: 'custom__class-dialog-database-use-authorization',
      disableClose: true,
    });

    this.eventSubcriberCloseModalDatabaseUseAuthorization();
  }

  eventSubcriberCloseModalDatabaseUseAuthorization() {
    this.dialogRefDatabaseUseAuthorization.afterClosed().subscribe((result) => {
      if ((typeof result === 'boolean' && !result) || !result) {
        this.doLogout();
      }
      this.dialogRefDatabaseUseAuthorization = null;
    });
  }

  getUserSession(token: string): Observable<ApiDetailResponse<UserSession>> {
    return this.http.get<ApiDetailResponse<UserSession>>(`${environment.newApiBackend}/api/auth/user-session`, { headers: new HttpHeaders().set(HEADER_FB_TOKEN, token) });
  }

  /** Prefer legacy buildings[0].roles; fall back to newFormat when login omits them. */
  private resolveLoginRoles(
    buildings: Array<{ roles?: string[] }>,
    userBuilding: { roles?: string[] | Array<{ getRoles?: () => string }> } | undefined,
    newFormat: { roles?: string[] } | undefined,
  ): string[] {
    const buildingRoles = buildings[0]?.roles;
    if (Array.isArray(buildingRoles) && buildingRoles.length > 0) {
      return buildingRoles;
    }
    const newFormatRoles = newFormat?.roles;
    if (Array.isArray(newFormatRoles) && newFormatRoles.length > 0) {
      return newFormatRoles;
    }
    const userBuildingRoles = userBuilding?.roles;
    if (Array.isArray(userBuildingRoles) && userBuildingRoles.length > 0) {
      return userBuildingRoles.map((role) =>
        typeof role === 'string' ? role : role?.getRoles?.() ?? String(role),
      );
    }
    return buildings.length > 0 ? ['superAdmin'] : ['superAdmin'];
  }

  /**
   * VDS-3387 — refresh buildings/roles/unit on the local snapshot from monorepo session.
   * Soft-fails: login/switch still succeed if the session GET fails.
   */
  async rehydrateCurrentUserFromSession(): Promise<boolean> {
    try {
      const current = this.getUser();
      const token = current?.fbToken;
      if (!token || current?.superUser) {
        return false;
      }
      const preferredBuildingId = current?.buildings?.[0]?.id ?? current?.buildings?.[0]?._id;
      const res = await this.getUserSession(token).toPromise();
      const session = (res as any)?.data ?? res;
      if (!session?.units?.length) {
        return false;
      }
      const merged = mergeUserSessionIntoCurrentUser(current, session, preferredBuildingId);
      this.setUser(merged);
      this.setUserInMultisesion(merged);
      return true;
    } catch (error) {
      console.error('rehydrateCurrentUserFromSession failed:', error);
      return false;
    }
  }

  deleteUserSession() {
    this.clearCurrentUserSession();
  }

  sendPasswordResetEmailV2(email: string): Promise<ApiDetailResponse<AssistantValidateEmailResponse>> {
    const url = `${environment.newApiBackend}/api/v2/users/public/assistant/validate-email`;
    const body = { email: email.toLowerCase().trim() };

    return firstValueFrom(
      this.http
        .post<ApiDetailResponse<AssistantValidateEmailResponse>>(url, body)
        .pipe(map((res) => unwrapNewApiEnvelope<ApiDetailResponse<AssistantValidateEmailResponse>>(res))),
    );
  }
}
